import { useSelector, useDispatch } from "react-redux";
import Select, { type SingleValue, type StylesConfig } from "react-select";
import * as Action from "../../state/actions/Actions";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { RootState } from "../../state/reducers/rootReducer";
import type { IOptionType } from "../../InterfacesTypes/interfaces";
import { STARTPAGETITLES, WEBSITE_SEARCH_URL } from "InterfacesTypes/enums";
import { CiSearch } from "react-icons/ci";
import { selectSpEnvironment, selectTitleTexts } from "state/selectors/Selectors";
import { TooltipComponent } from "components/toolTipComponent/TooltipComponent";

type Mode = "keywords" | "web";
const SelectSearchBar = ({
  selected,
  setSelected,
  inputVal,
  setInputVal,
}: {
  selected: IOptionType | null;
  setSelected: Dispatch<SetStateAction<IOptionType | null>>;
  inputVal: string;
  setInputVal: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const dispatch = useDispatch();
  const spEnv = useSelector(selectSpEnvironment);
  const startTitles = useSelector(selectTitleTexts);
  const [mode, setMode] = useState<Mode>("keywords");

  const { searchInput, matchingKeyWordsArray } = useSelector(
    (state: RootState) => state.State
  );

  const searchbarTooltip = startTitles.find(x => x.TitlePosition === STARTPAGETITLES.SEARCHBAR_TOOLTIP)?.Description ?? "";
  const inputRef = useRef<HTMLInputElement | null>(null);

  const options: IOptionType[] = useMemo(
    () =>
      matchingKeyWordsArray.map((word: string) => ({
        label: word.toUpperCase(),
        value: word.toUpperCase(),
      })),
    [matchingKeyWordsArray]
  );

  const getOptionLabel = (option: IOptionType) => option.label;
  const getOptionValue = (option: IOptionType) => option.value;

  const handleInputChange = (selectValue: string) => {
    dispatch(Action.searchKeyWords(selectValue));
    setInputVal(selectValue);
  };

  const handleSelectChange = (selectedOption: SingleValue<IOptionType>) => {
    setSelected(selectedOption ?? null);

    if (!selectedOption) {
      dispatch(Action.searchInputResetAction());
      dispatch(Action.setUserSelectedInputKeyWord({ label: "", value: "" }));
      return;
    }

    dispatch(Action.searchResults(selectedOption.label));
    dispatch(Action.setUserSelectedInputKeyWord(selectedOption));
  };

  const customStyles: StylesConfig<IOptionType, false> = {
    control: (provided) => ({
      ...provided,
      borderColor: "#918c8c",
    }),

    valueContainer: (provided) => ({
      ...provided,
      height: "50px",
      width: "300px",
      padding: "0 6px",
      boxShadow: "1px 1px 2px #797979",
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "30px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      color: "black",
      opacity: searchInput.length > 1 ? "1" : "0",
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    clearIndicator: (provided) => ({
      ...provided,
      display: mode === "web" ? "none" : "flex",
      marginTop: "20px",
      height: "40px",
      transform: "translate(0px, 1px)",
      color: "black",
    }),

    input: (provided) => ({
      ...provided,
      margin: "0px",
      "&:focus": {
        webkitUserSelect: "none",
        mozUserSelect: "none",
        msUserSelect: "none",
        userSelect: "none",
      },
    }),
  };
  const handleSearch = () => {
    const query = inputVal || searchInput;
    if (!query || query.trim() === "") return;

    if (mode === "web") {
      const url = `${WEBSITE_SEARCH_URL}=${encodeURIComponent(query)}`;
      window.location.href = url;
    } else {
      dispatch(Action.searchResults(query));
    }
  };
  useEffect(() => {
    const element = inputRef.current;
    if (!element) return;

    const disableZoom = () => {
      const viewport = document.querySelector("meta[name='viewport']");
      if (viewport) {
        viewport.setAttribute(
          "content",
          "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        );
      }
    };

    element.addEventListener("focus", disableZoom);
    return () => element.removeEventListener("focus", disableZoom);
  }, []);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSearch();
      }}
      id="startpageSearchForm"
      className="flex justify-start items-center flex-col [@media(min-width:1000px)]:!flex-row" 
    >
      <div className={`relative ${spEnv === "logistikportalen" ? "!w-[98%]" : "[@media(min-width:750px)]:!w-[65%] !w-full"} `}>
        <TooltipComponent
          tooltipClassName={`!w-full !max-w-full ${searchbarTooltip === "" ? "hidden" : ""}`}
          className="w-full"
          content={searchbarTooltip}
        >

          <Select
            inputId="keywordsInput"
            className="customSelect !w-full"
            styles={customStyles}
            placeholder="Vad vill du ha hjälp med att hitta?"
            value={selected}
            inputValue={inputVal}
            onChange={handleSelectChange}
            onInputChange={(value, { action }) => {
              if (action === "input-change") handleInputChange(value);
            }}
            menuIsOpen={mode === "keywords" ? true : false}
            openMenuOnClick={mode === "keywords" ? true : false}
            isClearable
            tabSelectsValue={false}
            options={mode === "keywords" ? options : []}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            noOptionsMessage={() => "Sökresultat saknas"}
          />
        </TooltipComponent>
        {mode === "web" && (
          <button
            type="submit"
            className="absolute inset-y-0 right-1 !min-w-[3em] flex items-center justify-center
               text-gray-600 hover:text-blue-600
               focus:outline-none focus:ring-0
               border-none bg-transparent !p-0 !m-0 appearance-none"
          >
            {mode === "web" && <CiSearch size={30} />}
          </button>
        )}
      </div>
      <div className="flex">
        {spEnv === "logp" &&
          <>
            <label className="!flex !justify-start !items-center !ml-4">
              <input
                type="radio"
                name="searchMode"
                value="keywords"
                checked={mode === "keywords"}
                onChange={() => setMode("keywords")}
                id="searchKeywords"
                className="!accent-[#003d4c] !w-[28px] !h-[20px] !transition-colors duration-700"
              />
              <span className="text-[rgb(44,43,43)] text-lg !transition-colors duration-700">
                Sök på nyckelord
              </span>
            </label>
            <label className="!flex !justify-start !items-center !ml-4">
              <input
                type="radio"
                name="searchMode"
                value="web"
                checked={mode === "web"}
                onChange={() => setMode("web")}
                id="searchWeb"
                className="!accent-[#003d4c] !w-[28px] !h-[20px] !transition-colors duration-700"
              />
              <span className="text-[rgb(44,43,43)] text-lg !transition-colors duration-700">
                Sök hela webbplatsen
              </span>
            </label>
          </>
        }
      </div>
    </form>
  );
};

export default SelectSearchBar;
